import { useState } from 'react';
import { toast } from 'sonner';
import { Save, ExternalLink, Loader2, FileWarning, UserX } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DOC_KEYS, STATE_STYLE, daysText, profileUrl } from './utils';

const addYears = (iso, n) => {
  const d = iso ? new Date(iso) : new Date();
  d.setFullYear(d.getFullYear() + n);
  return d.toISOString().slice(0, 10);
};

// Satırın altında açılan hızlı düzenleme: bitiş tarihleri + eksikler + profile kısayollar.
export function RowEditor({ row, onSaved }) {
  const [values, setValues] = useState(() => Object.fromEntries(DOC_KEYS.map((d) => [d.field, row.documents[d.key].date || ''])));
  const [saving, setSaving] = useState(false);
  const dirty = DOC_KEYS.some((d) => (values[d.field] || '') !== (row.documents[d.key].date || ''));

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(DOC_KEYS.filter((d) => (values[d.field] || '') !== (row.documents[d.key].date || '')).map((d) => [d.field, values[d.field] || null]));
      await api.patch(`/employee-profile/user/${row.user_id}/expiry-dates`, body);
      toast.success(`${row.full_name} — tarihler güncellendi`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr] p-4 bg-slate-50/80 dark:bg-slate-900/60 border-t border-slate-200 dark:border-slate-700" data-testid={`overview-editor-${row.user_id}`}>
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Belge bitiş tarihleri</h4>
        <div className="grid sm:grid-cols-3 gap-3">
          {DOC_KEYS.map((d) => {
            const doc = row.documents[d.key];
            return (
              <div key={d.key} className={`rounded-lg border p-2.5 ${STATE_STYLE[doc.state]}`}>
                <div className="text-[11px] font-semibold mb-1">{d.label}</div>
                <Input type="date" value={values[d.field]} onChange={(e) => setValues((v) => ({ ...v, [d.field]: e.target.value }))} className="h-8 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100" data-testid={`overview-date-${d.key}-${row.user_id}`} />
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px]">{daysText(doc.days)}</span>
                  <button type="button" onClick={() => setValues((v) => ({ ...v, [d.field]: addYears(v[d.field], 1) }))} className="text-[10px] font-semibold underline underline-offset-2" data-testid={`overview-plus1-${d.key}-${row.user_id}`}>+1 yıl</button>
                </div>
                {!doc.uploaded && <div className="text-[10px] mt-1 font-semibold text-red-600">Belge yüklenmemiş</div>}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end mt-3">
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid={`overview-save-${row.user_id}`}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Kaydet
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        {row.missing_docs.length > 0 && (
          <div>
            <h4 className="flex items-center gap-1 text-xs font-semibold text-red-600 mb-1"><FileWarning className="h-3.5 w-3.5" />Eksik belgeler</h4>
            <div className="flex flex-wrap gap-1">{row.missing_docs.map((m) => <span key={m} className="text-[11px] rounded-full bg-red-100 text-red-700 px-2 py-0.5">{m}</span>)}</div>
            <a href={profileUrl(row.user_id, 'documents')} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:underline mt-1.5" data-testid={`overview-upload-docs-${row.user_id}`}>
              Belgeleri yükle (yeni sekme) <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
        {row.missing_fields.length > 0 && (
          <div>
            <h4 className="flex items-center gap-1 text-xs font-semibold text-amber-600 mb-1"><UserX className="h-3.5 w-3.5" />Eksik profil bilgileri</h4>
            <div className="flex flex-wrap gap-1">{row.missing_fields.map((m) => <span key={m} className="text-[11px] rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">{m}</span>)}</div>
            <a href={profileUrl(row.user_id, 'personal')} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:underline mt-1.5" data-testid={`overview-fill-info-${row.user_id}`}>
              Bilgileri tamamla (yeni sekme) <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
        {row.missing_docs.length === 0 && row.missing_fields.length === 0 && (
          <p className="text-xs text-emerald-600 font-medium">Belge ve profil bilgileri eksiksiz.</p>
        )}
      </div>
    </div>
  );
}
